import { useQuery } from '@apollo/client/react';
import { useEffect } from 'react';

import {
  SPAN_REFETCH_BASKET,
  SPAN_REMOTE_BASKET_UPDATE,
} from '@constants/otel';
import { useSyncAppointment } from '@features/Remote/useSyncAppointment';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { EventTypes, emitter } from '@services/emitter';
import { useTrace } from '@services/otel';
import { useTrackingContextRefresh } from '@tracking/hooks/index';
import { getIsLoadingQuery } from '@utils/getIsLoadingQuery';
import { getBundleBasket } from '@utils/queries/getBasket';

import { formatSelectedServices } from '../utils/formatBasket/formatSelectedServices';
import { useFormatBasket } from './useFormatBasket';

interface UseBundleBasketProps {
  skipTrackingContextUpdate?: boolean;
}

const useBundleBasket = ({
  skipTrackingContextUpdate = false,
}: UseBundleBasketProps = {}) => {
  const { withTrace } = useTrace();
  const { basketId } = useBasketContext();
  const { notifyBasketUpdated } = useSyncAppointment();
  const { data, loading, error, networkStatus, refetch } = useQuery(
    getBundleBasket,
    {
      variables: { basketId },
    }
  );
  const { formattedBasket } = useFormatBasket(data ?? null);
  useTrackingContextRefresh(skipTrackingContextUpdate)(data, loading);

  const executeRefetch = () => {
    refetch();
    notifyBasketUpdated();
  };

  // this listener -- to refetch the basket on an update request from the remote
  // appointment event subscription -- is in a dependency-less useEffect here because
  // otherwise it would be initialized every single render, and thus fire for every single
  // initialization, multiplied by the emition count. Which is a lot,
  // ultimately. Ideally i'd want this somewhere static, but it would
  // necessitate a major refactor, such as moving a bunch of stuff into a
  // provider somewhere. But such work needs to be debated and planned so we'll
  // leave that for now, assuming it's worth doing at all.
  useEffect(() => {
    emitter.on(EventTypes.RemoteBasketUpdated, () => {
      withTrace(SPAN_REMOTE_BASKET_UPDATE, executeRefetch);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefetch = () => withTrace(SPAN_REFETCH_BASKET, executeRefetch);

  const { nonEnergyServiceCount, coreServiceCount, selectedServices } =
    formatSelectedServices(formattedBasket);

  return {
    basket: formattedBasket,
    basketRaw: data?.basket,
    basketData: data,
    error,
    loading: getIsLoadingQuery(networkStatus, data),
    nonEnergyServiceCount,
    selectedServices,
    coreServiceCount,
    refetch: handleRefetch,
  };
};

export { useBundleBasket };
